import clsx from "clsx";
import React, { FC, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pd/pd-ui-kit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { NOTIFICATIONS } from "~/constans/notifications";
import { useGetLocationData } from "~/hooks/locationData";
import { uploadFile } from "~/libs/uploadFile";
import {
  useAddNotificationMutation,
  useUploadFileCatalogMutation,
} from "~/schemas/generated/graphql";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { Field } from "~/shared/ui/Field";

import s from "./UploadForm.less";

const ACCEPT_MIME_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/tiff",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/svg+xml",
  "image/png",
  "image/webp",
  "image/bmp",
  "application/zip",
  "application/x-rar-compressed",
  "application/pdf",
  "image/gif",
  "text/plain",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

interface IProps {
  setCloseModal: () => void;
  refetch;
  /** id формы — чтобы кнопка submit могла жить в футере модалки (okButton form). */
  formId?: string;
  /** Рисовать submit-кнопку внутри формы (для потребителей со старой модалкой). */
  submitInForm?: boolean;
  /** Уведомляет о выборе/удалении файла — для управления okButton снаружи. */
  onFileChange?: (fileId: string | null) => void;
}

const PKDUploadFileForm: FC<IProps> = ({
  setCloseModal,
  refetch,
  formId,
  submitInForm = true,
  onFileChange,
}) => {
  const { t } = useTranslation();

  const [fileId, setFileId] = useState(null);
  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const { event } = useGetLocationData();
  const currentDealerId = useCurrentDealerId();
  const [addNotificationMutation] = useAddNotificationMutation({});
  const [uploadFileCatalog, FileUpload] = useUploadFileCatalogMutation({
    onCompleted() {
      refetch();
      addNotificationMutation({
        variables: {
          type: NOTIFICATIONS.success,
          content: t("common.file_uploaded_successfully"),
        },
      });
    },
    onError() {
      setCloseModal();
    },
  });

  const schema = yup.object().shape({
    name: yup
      .string()
      .max(250, t("validation.more250"))
      .required(t("validation.required")),
  });

  const { handleSubmit, control, errors } = useForm({
    resolver: yupResolver(schema),
  });

  const fallBack = () => {
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: t("common.data_uploaded_error"),
      },
    });
  };

  const errorNotification = () => {
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: t("common.data_uploaded_error_size"),
      },
    });
  };

  const onSubmit = async ({ name }) => {
    if (!fileId) return;

    setCloseModal();

    await uploadFileCatalog({
      variables: {
        fileId,
        catalog: event,
        DealerId: currentDealerId,
        name,
      },
    });
  };

  const isDisable = !!fileId;

  const handleFileChange = (id: string | null) => {
    setFileId(id);
    onFileChange?.(id);
  };

  return (
    <form id={formId} className={s.upl_form} onSubmit={handleSubmit(onSubmit)}>
      <ul className={s.list}>
        <li>
          <Field
            type="input"
            name="name"
            label={t("pkd.docName")}
            control={control}
            error={errors["name"]?.message}
          />
        </li>

        <li className={s.with_btn}>
          <Field
            type="file"
            name="file"
            control={control}
            error={errors["file"]?.message}
            accept={ACCEPT_MIME_TYPES}
            loading={uploadFileLoader}
            className={s.fileField}
            onUpload={async (file) => {
              setUploadFileLoader(true);
              try {
                const uploaded = await uploadFile(file, fallBack);
                if (uploaded) {
                  handleFileChange(uploaded.id);
                  return {
                    id: uploaded.id,
                    name: uploaded.name,
                    link: uploaded.link,
                    mimeType: uploaded.mimeType,
                  };
                }
              } catch (error) {
                if (error.response?.data?.message === "File too large") {
                  errorNotification();
                } else {
                  fallBack();
                }
              } finally {
                setUploadFileLoader(false);
              }
            }}
            onDelete={() => handleFileChange(null)}
          />
        </li>

        <li
          className={clsx(s.text_drop, {
            [s.text_drop_file]: !isDisable,
          })}
        />
        {submitInForm && (
          <li className={s.with_btn}>
            <Button
              htmlType="submit"
              size={"middle"}
              loading={FileUpload.loading}
              disabled={!isDisable}
            >
              {t("ui.upload_file")}
            </Button>
          </li>
        )}
      </ul>
    </form>
  );
};
export default PKDUploadFileForm;
